import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from '../../forum/app';
import Page from '../../common/components/Page';
import ItemList from '../../common/utils/ItemList';
import DiscussionHero from './DiscussionHero';
import DiscussionListPane from './DiscussionListPane';
import LoadingIndicator from '../../common/components/LoadingIndicator';
import SplitDropdown from '../../common/components/SplitDropdown';
import listItems from '../../common/helpers/listItems';
import DiscussionControls from '../utils/DiscussionControls';
import PostStreamState from '../states/PostStreamState';
import PageStructure from './PageStructure';
/**
 * The `DiscussionPage` component displays a whole discussion page, including
 * the discussion list pane, the hero, the posts, and the sidebar.
 */
export default class DiscussionPage extends Page {
  constructor() {
    super(...arguments);
    _defineProperty(this, "loading", true);
    _defineProperty(this, "PostStream", null);
    _defineProperty(this, "PostStreamScrubber", null);
    /**
     * The discussion that is being viewed.
     */
    _defineProperty(this, "discussion", null);
    /**
     * A public API for interacting with the post stream.
     */
    _defineProperty(this, "stream", null);
    /**
     * The number of the first post that is currently visible in the viewport.
     */
    _defineProperty(this, "near", 0);
    _defineProperty(this, "useBrowserScrollRestoration", true);
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.load();

    // If the discussion list has been loaded, then we'll enable the pane (and
    // hide it by default). Also, if we've just come from another discussion
    // page, then we don't want Mithril to redraw the whole page – if it did,
    // then the pane would redraw which would be slow and would cause problems with
    // event handlers.
    // We will also enable the pane if the discussion list is empty but loading,
    // because the DiscussionComposer refreshes the list and redirects to the new discussion at the same time.
    if (app.discussions.hasItems() || app.discussions.isLoading()) {
      var _app$pane, _app$pane2;
      (_app$pane = app.pane) == null || _app$pane.enable();
      (_app$pane2 = app.pane) == null || _app$pane2.hide();
    }
    this.bodyClass = 'App--discussion';
  }
  onremove(vnode) {
    var _app$pane3, _app$composer;
    super.onremove(vnode);

    // If we are indeed navigating away from this discussion, then disable the
    // discussion list pane. Also, if we're composing a reply to this
    // discussion, minimize the composer – unless it's empty, in which case
    // we'll just close it.
    (_app$pane3 = app.pane) == null || _app$pane3.disable();
    if (this.discussion && app.composer.composingReplyTo(this.discussion) && !((_app$composer = app.composer) != null && (_app$composer = _app$composer.fields) != null && _app$composer.content())) {
      app.composer.hide();
    } else {
      app.composer.minimize();
    }
  }
  view() {
    if (this.loading || !this.discussion) {
      return m(LoadingIndicator, null);
    }
    return m(PageStructure, {
      className: "DiscussionPage",
      loading: this.loading,
      hero: this.hero.bind(this),
      sidebar: this.sidebar.bind(this),
      pane: () => m(DiscussionListPane, {
        state: app.discussions
      })
    }, this.loading || m("div", {
      className: "DiscussionPage-stream"
    }, m(this.PostStream, {
      discussion: this.discussion,
      stream: this.stream,
      onPositionChange: this.positionChanged.bind(this)
    })));
  }

  /**
   * Function that renders the `sidebarItems` ItemList.
   */
  sidebar() {
    return m("nav", {
      className: "DiscussionPage-nav"
    }, m("ul", null, listItems(this.sidebarItems().toArray())));
  }

  /**
   * Renders the discussion's hero.
   */
  hero() {
    return m(DiscussionHero, {
      discussion: this.discussion
    });
  }

  /**
   * Load the discussion from the API or use the preloaded one.
   */
  load() {
    Promise.all([import(/* webpackChunkName: 'forum/components/PostStream', webpackMode: 'lazy-once' */ './PostStream'), import(/* webpackChunkName: 'forum/components/PostStreamScrubber', webpackMode: 'lazy-once' */ './PostStreamScrubber')]).then(_ref => {
      let [PostStreamImport, PostStreamScrubberImport] = _ref;
      this.PostStream = PostStreamImport.default;
      this.PostStreamScrubber = PostStreamScrubberImport.default;
      const preloadedDiscussion = app.preloadedApiDocument();
      if (preloadedDiscussion) {
        // We must wrap this in a setTimeout because if we are mounting this
        // component for the first time on page load, then any calls to m.redraw
        // will be ineffective and thus any configs (scroll code) will be run
        // before stuff is drawn to the page.
        setTimeout(this.show.bind(this, preloadedDiscussion), 0);
      } else {
        const params = this.requestParams();
        app.store.find('discussions', m.route.param('id'), params).then(this.show.bind(this));
      }
    });
    m.redraw();
  }

  /**
   * Get the parameters that should be passed in the API request to get the
   * discussion.
   */
  requestParams() {
    return {
      bySlug: true,
      page: {
        near: this.near
      }
    };
  }

  /**
   * Initialize the component to display the given discussion.
   */
  show(discussion) {
    this.loading = false;
    app.history.push('discussion', discussion.title());
    app.setTitle(discussion.title());
    app.setTitleCount(0);

    // Set up the post stream for this discussion, along with the first page of
    // posts we want to display. Tell the stream to scroll down and highlight
    // the specific post that was routed to.
    this.stream = new PostStreamState(discussion);
    const rawNearParam = m.route.param('near');
    const nearParam = rawNearParam === 'reply' ? 'reply' : parseInt(rawNearParam);
    // Post numbers start at 1; use 1 when near is missing/invalid so the first post gets the pop-in
    const targetPost = nearParam === 'reply' ? 'reply' : nearParam && !isNaN(nearParam) ? nearParam : 1;
    this.stream.goToNumber(targetPost, true).then(() => {
      this.discussion = discussion;
      app.current.set('discussion', discussion);
      app.current.set('stream', this.stream);
    });
  }

  /**
   * Build an item list for the contents of the sidebar.
   */
  sidebarItems() {
    const items = new ItemList();
    if (this.discussion) {
      items.add('controls', m(SplitDropdown, {
        icon: "fas fa-ellipsis-v",
        className: "App-primaryControl",
        buttonClassName: "Button--primary",
        accessibleToggleLabel: app.translator.trans('core.forum.discussion_controls.toggle_dropdown_accessible_label')
      }, DiscussionControls.controls(this.discussion, this).toArray()), 100);
    }
    items.add('scrubber', m(this.PostStreamScrubber, {
      stream: this.stream,
      className: "App-titleControl"
    }), -100);
    return items;
  }

  /**
   * When the posts that are visible in the post stream change (i.e. the user
   * scrolls up or down), then we update the URL and mark the posts as read.
   *
   * URL and history writes are skipped when startNumber hasn't changed from the
   * last known position — this prevents double history churn from the immediate
   * post-scroll emit and the subsequent settle-end reconciliation both resolving
   * to the same post. Read-state saves are intentionally independent of this
   * guard and use their own endNumber condition.
   */
  positionChanged(startNumber, endNumber) {
    const discussion = this.discussion;
    if (!discussion) return;

    // Only write history when the visible start post actually changed.
    if (startNumber !== this.near) {
      // Construct a URL to this discussion with the updated position, then
      // replace it into the window's history and our own history stack.
      const url = app.route.discussion(discussion, startNumber);
      this.near = startNumber;
      window.history.replaceState(null, document.title, url);
      app.history.push('discussion', discussion.title());
    }

    // If the user hasn't read past here before, then we'll update their read
    // state and redraw.
    if (app.session.user && endNumber > (discussion.lastReadPostNumber() || 0)) {
      discussion.save({
        lastReadPostNumber: endNumber
      });
      m.redraw();
    }
  }
}
flarum.reg.add('core', 'forum/components/DiscussionPage', DiscussionPage);flarum.reg.addChunkModule('158', '3038', 'core', 'forum/components/PostStream');
flarum.reg.addChunkModule('158', '3038', 'core', 'forum/components/ReplyPlaceholder');
flarum.reg.addChunkModule('158', '3038', 'core', 'forum/components/PostType');
flarum.reg.addChunkModule('486', '8857', 'core', 'forum/components/PostStreamScrubber');